import {
  authenticatedFastApiAxiosInstance,
  authenticatedFastApiAxiosInstanceAllowEmptyString,
} from "@/lib/data/http";
import type { MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  type APIResponse,
  type MassTort,
  type MassTortCustomField,
  type MassTortGeneratedFileTemplateRequest,
  type MassTortMatterCustomField,
  type MassTortMatterSearch,
  type MassTortSearch,
  type MassTortTemplateGenerationRetryResult,
} from "@/lib/types";

const MAX_SEARCH_PAGES = 50;

export type MassTortMatterSearchStatusCategory =
  (typeof MATTER_STATUS_CATEGORY_OPTIONS)[number];

export type CreateMassTortV1Input = {
  title: string;
  description?: string | null;
};

export type UpdateMassTortV1Input = {
  id: string;
  dateModified: string;
  description?: string | null;
} & Partial<Pick<MassTort, "title">>;

export type CreateMassTortCustomFieldV1Input = {
  massTortId: string;
} & Pick<
  MassTortCustomField,
  "description" | "label" | "valueOptions" | "valueType"
>;

export type UpdateMassTortCustomFieldsV1Input = {
  massTortId: string;
  customFields: Array<
    Pick<MassTortCustomField, "dateModified" | "id"> &
      Partial<
        Pick<
          MassTortCustomField,
          "description" | "label" | "order" | "valueOptions" | "valueType"
        >
      >
  >;
};

export type UpdateMassTortMatterCustomFieldsV1Input = {
  matterId: string;
  customFields: Array<{
    dateModified: string;
    id: string;
    value: string;
  }>;
};

export type GenerateMassTortMatterTemplateDocumentsV1Input = {
  extension: ".pdf" | ".docx" | ".doc";
  fileId?: string | null;
  idLocation: string | null;
  massTortId: string;
  matterIds?: string[];
  name: string;
  templateId?: string | null;
  templateInputs?: Record<string, unknown> | null;
};

export type RetrieveMassTortTemplateGenerationRequestV1Input = {
  massTortId: string;
  requestId: string;
};

export type RetrieveMassTortTemplateGenerationRequestsV1Input = {
  massTortId: string;
};

export type RetryFailedMassTortTemplateGenerationJobsV1Input = {
  templateInputs?: Record<string, unknown> | null;
  templateInputsByMatterId?: Record<string, Record<string, unknown>> | null;
  requests: {
    massTortId: string;
    requestId: string;
    matterIds?: string[];
    templateInputs?: Record<string, unknown> | null;
    templateInputsByMatterId?: Record<string, Record<string, unknown>> | null;
  }[];
};

/** POST body for `/matters/v2/mass-torts/search/matters`. */
export type RetrieveMassTortMatterSearchInput = {
  massTortId: string;
  statusCategory: MassTortMatterSearchStatusCategory;
};

export function createMassTortV1(data: CreateMassTortV1Input) {
  return authenticatedFastApiAxiosInstance
    .post<APIResponse<MassTort>>(`/matters/v2/mass-torts`, data)
    .then((res) => res.data);
}

export function updateMassTortV1(data: UpdateMassTortV1Input) {
  return authenticatedFastApiAxiosInstance
    .patch<APIResponse<MassTort>>(`/matters/v2/mass-torts`, data)
    .then((res) => res.data);
}

export function deleteMassTortV1(data: { massTortId: string }) {
  const { massTortId } = data;
  return authenticatedFastApiAxiosInstance
    .delete<APIResponse<MassTort>>(`/matters/v2/mass-torts/${massTortId}`)
    .then((res) => res.data);
}

export function retrieveMassTortV3(massTortId: string) {
  return authenticatedFastApiAxiosInstance
    .get<APIResponse<MassTort>>(`/matters/v3/mass-torts/${massTortId}`)
    .then((res) => res.data);
}

export function createMassTortCustomFieldV1(
  data: CreateMassTortCustomFieldV1Input,
) {
  return authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MassTortCustomField>
    >(`/matters/v2/mass-torts/custom-fields`, data)
    .then((res) => res.data);
}

export function updateMassTortCustomFieldsV1(
  data: UpdateMassTortCustomFieldsV1Input,
) {
  return authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<MassTortCustomField[]>
    >(`/matters/v2/mass-torts/custom-fields`, data)
    .then((res) => res.data);
}

export function deleteMassTortCustomFieldV1(data: {
  massTortId: string;
  customFieldId: string;
}) {
  const { massTortId, customFieldId } = data;
  return authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<MassTortCustomField>
    >(`/matters/v2/mass-torts/${massTortId}/custom-fields/${customFieldId}`)
    .then((res) => res.data);
}

export function updateMassTortMatterCustomFieldsV1(
  data: UpdateMassTortMatterCustomFieldsV1Input,
) {
  return authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<MassTortMatterCustomField>
    >(`/matters/v2/mass-torts/matters/custom-fields`, data)
    .then((res) => res.data);
}

export function generateMassTortMatterTemplateDocumentsV1(
  data: GenerateMassTortMatterTemplateDocumentsV1Input,
) {
  const { idLocation, ...rest } = data;

  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<APIResponse<MassTortGeneratedFileTemplateRequest>>(
      `/matters/v2/mass-torts/template-generation`,
      {
        ...rest,
        idLocation: idLocation ?? "",
      },
    )
    .then((res) => res.data);
}

export function retrieveMassTortTemplateGenerationRequestV1({
  massTortId,
  requestId,
}: RetrieveMassTortTemplateGenerationRequestV1Input) {
  return authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MassTortGeneratedFileTemplateRequest>
    >(`/matters/v2/mass-torts/template-generation/${massTortId}/${requestId}`)
    .then((res) => res.data);
}

export function retrieveMassTortTemplateGenerationRequestsV1({
  massTortId,
}: RetrieveMassTortTemplateGenerationRequestsV1Input) {
  return authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MassTortGeneratedFileTemplateRequest[]>
    >(`/matters/v2/mass-torts/template-generation/${massTortId}`)
    .then((res) => res.data);
}

export function retryFailedMassTortTemplateGenerationJobsV1(
  data: RetryFailedMassTortTemplateGenerationJobsV1Input,
) {
  return authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MassTortTemplateGenerationRetryResult>
    >(`/matters/v2/mass-torts/template-generation/retry`, data)
    .then((res) => res.data);
}

export const retrieveMassTortSearchV1 = async () => {
  const massTorts: MassTortSearch[] = [];
  let cursor: string | undefined;
  let pageCount = 0;
  let latestResponse: APIResponse<MassTortSearch[]> = {
    data: [],
    meta: { userMsg: "" },
  };

  do {
    latestResponse = await authenticatedFastApiAxiosInstance
      .post<
        APIResponse<MassTortSearch[]>
      >(`/matters/v1/mass-torts/search`, { cursor, limit: 500 })
      .then((res) => res.data);
    massTorts.push(...latestResponse.data);
    cursor = latestResponse.pagination?.nextCursor;
    pageCount += 1;
  } while (cursor && pageCount < MAX_SEARCH_PAGES);

  return {
    data: massTorts,
    meta: latestResponse.meta,
  } satisfies APIResponse<MassTortSearch[]>;
};

export const retrieveMassTortMatterSearchV1 = async (
  body: RetrieveMassTortMatterSearchInput,
) => {
  let cursor: string | undefined;
  let pageCount = 0;
  let latestMeta: APIResponse<MassTortMatterSearch[]>["meta"] = {
    userMsg: "",
  };
  const items: MassTortMatterSearch[] = [];

  do {
    const response = await authenticatedFastApiAxiosInstance.post<
      APIResponse<MassTortMatterSearch[]>
    >(`/matters/v2/mass-torts/search/matters`, body, {
      params: { cursor, limit: 2500 },
    });
    const page = response.data;
    latestMeta = page.meta;
    items.push(...page.data);
    cursor = page.pagination?.nextCursor;
    pageCount += 1;
  } while (cursor && pageCount < MAX_SEARCH_PAGES);

  return { data: items, meta: latestMeta } satisfies APIResponse<
    MassTortMatterSearch[]
  >;
};
